import React, { useMemo, useState } from 'react';
import { useFetchUsers } from '../../hooks/DataFetching/useFetchUsers';
import { useFetchCommunityPeople } from '../../hooks/DataFetching/useFetchCommunityPeople';
import { useFetchFriendRequests } from '../../hooks/DataFetching/useFetchFriendRequests';
import { useFetchFriends } from '../../hooks/DataFetching/useFetchFriends';
import { useFriendRequest } from '../../hooks/useFriendRequest';
import { Button, Input, InputGroup, InputGroupText, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import '../../styles/components/UserSearch.css';
import { useAuth0 } from '@auth0/auth0-react';

const normalizeUserId = (userId) => (userId ? userId.replace('|', '').replace('-', '') : '');

export const UserSearch = () => {
    const [searchInput, setSearchInput] = useState('');
    const [searchString, setSearchString] = useState('');
    const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
    const [confirmationName, setConfirmationName] = useState('');
    const { users, loading: searchLoading } = useFetchUsers(searchString, searchString.trim() !== '');
    const { people, loading: communityLoading, error: communityError } = useFetchCommunityPeople();
    const { friendRequests, setFriendRequests } = useFetchFriendRequests();
    const { friends } = useFetchFriends();
    const { postFriendRequest, error } = useFriendRequest();
    const { user } = useAuth0();

    const currentUserId = normalizeUserId(user?.sub);
    const communityLookup = useMemo(() => new Map(
        people.map((person) => [normalizeUserId(person.auth0UserId), person.sharedCommunities || []])
    ), [people]);

    const handleSearch = (event) => {
        event.preventDefault();
        setSearchString(searchInput.trim());
    };

    const displayName = (selectedUser) => selectedUser.displayName ||
        `${selectedUser.userFname || ''} ${selectedUser.userLname || ''}`.trim() ||
        'Member';

    const handleSendFriendRequest = async (selectedUser) => {
        const payload = {
            id: 0,
            requesterId: user.sub,
            recipientId: selectedUser.auth0UserId,
            status: 'Pending',
            requestSentOn: new Date().toISOString(),
            requestRespondedOn: null,
        };

        const wasSuccessful = await postFriendRequest(payload);
        if (wasSuccessful) {
            setConfirmationName(displayName(selectedUser));
            setIsConfirmationOpen(true);
            setFriendRequests((prevRequests) => [
                ...prevRequests,
                {
                    requesterId: currentUserId,
                    recipientId: normalizeUserId(selectedUser.auth0UserId),
                    status: 'Pending',
                },
            ]);
        }
    };

    const getFriendStatus = (selectedUser) => {
        const selectedUserId = normalizeUserId(selectedUser.auth0UserId);
        const isFriend = friends.some((friend) =>
            (normalizeUserId(friend.requesterId) === currentUserId
                && normalizeUserId(friend.recipientId) === selectedUserId)
            || (normalizeUserId(friend.requesterId) === selectedUserId
                && normalizeUserId(friend.recipientId) === currentUserId)
        );

        if (isFriend) return 'Connected';

        const hasPendingRequest = friendRequests.some((request) =>
            (normalizeUserId(request.requesterId) === currentUserId
                && normalizeUserId(request.recipientId) === selectedUserId)
            || (normalizeUserId(request.requesterId) === selectedUserId
                && normalizeUserId(request.recipientId) === currentUserId)
        );

        return hasPendingRequest ? 'Request sent' : null;
    };

    const renderPerson = (selectedUser, suppliedCommunities = []) => {
        const name = displayName(selectedUser);
        const communities = suppliedCommunities.length > 0
            ? suppliedCommunities
            : communityLookup.get(normalizeUserId(selectedUser.auth0UserId)) || [];
        const status = getFriendStatus(selectedUser);
        const initials = name.split(/\s+/).slice(0, 2).map((part) => part.charAt(0)).join('').toUpperCase() || 'M';

        return (
            <article className="community-person-card" key={selectedUser.userId || selectedUser.auth0UserId}>
                <div className="community-person-card__header">
                    {selectedUser.profilePictureUrl ? (
                        <img className="community-person-avatar" src={selectedUser.profilePictureUrl} alt="" />
                    ) : (
                        <div className="community-person-avatar community-person-avatar--fallback" aria-hidden="true">
                            {initials}
                        </div>
                    )}
                    <div>
                        <h3>{name}</h3>
                        {communities.length > 0 && (
                            <p>{communities.length} shared {communities.length === 1 ? 'community' : 'communities'}</p>
                        )}
                    </div>
                </div>
                <p className="community-person-bio">{selectedUser.userBio || 'A fellow member of the TYK community.'}</p>
                {communities.length > 0 && (
                    <div className="shared-community-list" aria-label={`Communities shared with ${name}`}>
                        {communities.slice(0, 3).map((community) => (
                            <span
                                className={community.isGeographic ? 'shared-community-chip shared-community-chip--place' : 'shared-community-chip'}
                                key={community.groupId}
                            >
                                {community.isGeographic ? '⌖ ' : ''}{community.groupName}
                            </span>
                        ))}
                        {communities.length > 3 && <span className="shared-community-more">+{communities.length - 3} more</span>}
                    </div>
                )}
                <Button
                    className="community-connect-button"
                    onClick={() => handleSendFriendRequest(selectedUser)}
                    disabled={status !== null}
                >
                    {status || 'Connect'}
                </Button>
            </article>
        );
    };

    const searchResults = users.filter((candidate) =>
        normalizeUserId(candidate.auth0UserId) !== currentUserId
    );

    return (
        <main className="people-discovery-page">
            <section className="people-discovery-hero">
                <p className="people-discovery-eyebrow">THE PEOPLE AROUND YOU</p>
                <h1>People in your communities</h1>
                <p>
                    Discover people who share your churches, groups, and local communities. Sharing a community
                    helps you find one another; becoming friends still requires a request and acceptance.
                </p>
            </section>

            <form className="people-search-box" onSubmit={handleSearch}>
                <div>
                    <label htmlFor="people-search">Find someone by name</label>
                    <InputGroup className="people-search-input-group">
                        <InputGroupText aria-hidden="true">⌕</InputGroupText>
                        <Input
                            id="people-search"
                            type="search"
                            placeholder="First or last name"
                            value={searchInput}
                            onChange={(event) => setSearchInput(event.target.value)}
                        />
                    </InputGroup>
                </div>
                <Button type="submit" disabled={!searchInput.trim()}>Search all members</Button>
            </form>

            {searchString && (
                <section className="people-section" aria-labelledby="member-search-heading">
                    <div className="people-section-heading">
                        <div>
                            <p className="people-section-kicker">SEARCH RESULTS</p>
                            <h2 id="member-search-heading">Members matching “{searchString}”</h2>
                        </div>
                        <button className="clear-people-search" type="button" onClick={() => {
                            setSearchString('');
                            setSearchInput('');
                        }}>
                            Clear search
                        </button>
                    </div>
                    {searchLoading ? (
                        <div className="people-state">Searching...</div>
                    ) : searchResults.length > 0 ? (
                        <div className="community-people-grid">{searchResults.map((person) => renderPerson(person))}</div>
                    ) : (
                        <div className="people-state">No members matched that name.</div>
                    )}
                </section>
            )}

            <section className="people-section" aria-labelledby="shared-community-heading">
                <div className="people-section-heading">
                    <div>
                        <p className="people-section-kicker">SHARED COMMUNITY</p>
                        <h2 id="shared-community-heading">People you already have something in common with</h2>
                        <p>Closest shared circles are shown first, including your profile-managed local communities.</p>
                    </div>
                    {!communityLoading && !communityError && <span className="people-count">{people.length} people</span>}
                </div>
                {communityLoading ? (
                    <div className="people-state">Gathering people from your communities...</div>
                ) : communityError ? (
                    <div className="people-state people-state--error">{communityError}</div>
                ) : people.length > 0 ? (
                    <div className="community-people-grid">
                        {people.map((person) => renderPerson(person, person.sharedCommunities || []))}
                    </div>
                ) : (
                    <div className="people-state">
                        <span aria-hidden="true">◎</span>
                        <h3>Your community connections will appear here</h3>
                        <p>Join a church, group, or complete your location profile to begin discovering people nearby.</p>
                    </div>
                )}
            </section>

            {error && <p className="people-inline-error">Error: {error}</p>}

            <Modal
                isOpen={isConfirmationOpen}
                toggle={() => setIsConfirmationOpen(false)}
                centered
                aria-labelledby="friend-request-confirmation-title"
                className="friend-request-modal"
                contentClassName="friend-request-modal-content"
            >
                <ModalHeader toggle={() => setIsConfirmationOpen(false)} id="friend-request-confirmation-title">
                    Request sent
                </ModalHeader>
                <ModalBody>
                    Your friend request to {confirmationName} has been sent. We&apos;ll let you know when they respond.
                </ModalBody>
                <ModalFooter>
                    <Button color="primary" onClick={() => setIsConfirmationOpen(false)}>Close</Button>
                </ModalFooter>
            </Modal>
        </main>
    );
};
